
import { useRef, type ReactNode } from "react";
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  MapPinned,
  Radio,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PartnerForm, { type PartnerFormValues } from "@/components/forms/PartnerForm";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { createAdminPartner, type AdminPartnerRecord } from "@/api/adminPartner";
import { usePartners } from "@/context/PartnerContext";

function ReadinessCard({
  icon,
  label,
  title,
  detail,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  detail: string;
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
        {icon}
        {label}
      </div>
      <p className="mt-2 text-base font-bold text-slate-950">{title}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

const NewPartner = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { refreshPartners } = usePartners();
  const createdPartnerId = useRef("");

  const handleSuccess = () => {
    toast({
      title: "Ο συνεργάτης δημιουργήθηκε",
      description: "Η εγγραφή είναι έτοιμη για έλεγχο χωρίς αλλαγή στις ιστορικές αναθέσεις.",
    });
    navigate(createdPartnerId.current
      ? `/admin/partners/${encodeURIComponent(createdPartnerId.current)}/dashboard`
      : "/admin/partners");
  };

  const createPartner = async (values: PartnerFormValues) => {
    const saved = await createAdminPartner({
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      location: values.location.trim(),
      miniImageUrl: values.miniImageUrl?.trim() || null,
      miniDescription: values.miniDescription?.trim() || null,
      services: values.services as AdminPartnerRecord["services"],
      active: values.active,
      smsOptOut: values.sms_opt_out,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      radius: values.radius,
    });
    createdPartnerId.current = saved.id;
    await refreshPartners();
  };

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Δίκτυο συνεργατών · Νέα εγγραφή"
        title="Νέος συνεργάτης"
        description="Ορίστε στοιχεία επικοινωνίας, υπηρεσίες routing και πραγματική περιοχή κάλυψης πριν ενεργοποιηθεί στη ροή νέων leads."
        icon={<UserPlus />}
        iconGradient="emerald"
        actions={
          <Button asChild variant="outline" className="h-11 w-full gap-2 sm:w-auto">
            <Link to="/admin/partners"><ArrowLeft className="h-4 w-4" /> Συνεργάτες</Link>
          </Button>
        }
      />

      <section aria-label="Έλεγχοι πριν τη δημιουργία" className="grid gap-3 sm:grid-cols-3">
        <ReadinessCard icon={<MapPinned className="h-4 w-4 text-sky-700" />} label="Routing" title="Υπηρεσία + περιοχή" detail="Το ακριβές σημείο και η ακτίνα καθορίζουν τις μελλοντικές αναθέσεις." />
        <ReadinessCard icon={<BellRing className="h-4 w-4 text-violet-700" />} label="Ειδοποιήσεις" title="Τηλέφωνο πρώτα" detail="Το email είναι προαιρετικό· το SMS ακολουθεί τη ρύθμιση opt-out." />
        <ReadinessCard icon={<ShieldCheck className="h-4 w-4 text-emerald-700" />} label="Portal" title="Χωρίς υποχρεωτικό login" detail="Τα ασφαλή links κάθε lead συνεχίζουν να ανοίγουν χωρίς κωδικό." />
      </section>

      <Card className="rounded-2xl border-sky-200 bg-sky-50/70 shadow-none">
        <CardContent className="flex gap-3 p-4 sm:p-5">
          <Radio className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
          <div>
            <h2 className="text-sm font-semibold text-sky-950">Τι συμβαίνει μετά την αποθήκευση</h2>
            <div className="mt-2 grid gap-1 text-sm leading-6 text-sky-900 sm:grid-cols-3 sm:gap-4">
              <p className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0" /> Ενεργός και routing-ready συνεργάτης συμμετέχει μόνο σε επόμενα επιλέξιμα leads.</p>
              <p className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0" /> Οι ιστορικές αναθέσεις και οι ήδη σταλμένες ειδοποιήσεις δεν αλλάζουν.</p>
              <p className="flex gap-2"><CheckCircle2 className="mt-1 h-4 w-4 shrink-0" /> Δεν αποστέλλεται setup email ή SMS μόνο και μόνο επειδή δημιουργήθηκε η εγγραφή.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <PartnerForm
        onSuccess={handleSuccess}
        isAdminView
        submitPartner={createPartner}
        submitLabel="Δημιουργία συνεργάτη"
      />
    </div>
  );
};

export default NewPartner;
